/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * MapView — top-level county map landing page.
 *
 * Fully wired to live data as of 2026-04-10. On mount we fire three
 * authenticated requests in parallel:
 *   GET /voters/stats                      — county headline totals
 *   GET /voters/by-legislative-district    — LD cards
 *   GET /voters/by-precinct?limit=200      — precinct rows (grouped
 *                                              into subregions client-side
 *                                              via the tenant's turf catalog)
 *
 * Subregion / LD turf geometry comes from GET /geo/regions (MapRegionsContext);
 * walking-route blobs still come from ROUTES / GMAPS. Both are turf-assignment
 * constructs, not voter-roll attributes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ROUTES, GMAPS, LD_COLORS, LD_TEXT_COLORS } from '@/data/dashboardData';
import { useMapRegions } from '@/context/MapRegionsContext';
import { LD_NAMES } from '@/lib/computeStats';
import client from '@/api/client';
import { InfoTip } from '@/components/InfoTip';
import { MetricLabel } from '@/components/MetricLabel';
import { TierLegend } from '@/components/TierLegend';
import { VisualKey } from '@/components/VisualKey';
import { TierPill } from '@/components/TierPill';
import { BarMeter } from '@/components/BarMeter';
import { RankBadge } from '@/components/RankBadge';
import { computePeerBaselines, rankWithin } from '@/lib/baselines';
import {
  OnboardingModal,
  OnboardingHelpButton,
  hasSeenOnboarding,
} from '@/components/OnboardingModal';
import { CoachMarks } from '@/components/onboarding/CoachMarks';
import { MAP_TOUR } from '@/components/onboarding/tours';
import { useOnboarding } from '@/hooks/useOnboarding';
import { Skeleton, StatsGridSkeleton } from '@/components/Skeleton';
import { ViewHeader } from '@/components/ViewHeader';
import { SubregionTable, type SubregionSortKey, type SubregionSortDir } from '@/components/SubregionTable';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

// -------------------------------------------------------------------------
// Types mirroring backend/app/schemas/voter.py
// -------------------------------------------------------------------------

type VoterStats = {
  total_voters: number;
  active_voters: number;
  average_score: number;
  by_tier: Record<string, number>;
  by_party: Record<string, number>;
  primary_voters: number;
  high_scorers: number;
};

type LegislativeDistrictStats = {
  district_key: string;
  district_raw: string;
  total_voters: number;
  avg_score: number;
  active_pct: number;
  super_voter_pct: number;
  primary_voter_pct: number;
};

type PrecinctStats = {
  precinct_num: string;
  precinct_name: string;
  total_voters: number;
  active_voters: number;
  primary_voters: number;
  avg_score: number;
  super_voters: number;
  hot: number;
  warm: number;
  cool: number;
  super_voter_pct: number;
};

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

// -------------------------------------------------------------------------
// Main view
// -------------------------------------------------------------------------

export const MapView: React.FC<any> = ({ onDrill }) => {
  const [stats, setStats] = useState<VoterStats | null>(null);
  const [ldStats, setLdStats] = useState<LegislativeDistrictStats[] | null>(null);
  const [precincts, setPrecincts] = useState<PrecinctStats[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forceOnboard, setForceOnboard] = useState(false);

  // Turfs are tenant data now (GET /geo/regions). This view's LD cards and
  // Subregion Map are meaningless without them, so it renders an empty state
  // rather than a half-populated county.
  //
  // Two signals, deliberately: `/auth/me` carries
  // `tenant_map_regions_configured`, which resolves before the turf fetch does
  // and lets an unconfigured tenant skip straight to the empty state instead of
  // sitting through a skeleton for content that will never arrive. The catalog
  // itself is the authority once it lands — a tenant flagged configured whose
  // catalog comes back empty still gets the empty state, because there is
  // nothing left to fall back to. Older payloads omit the flag; `!== false`
  // keeps them on the catalog's answer alone.
  const { user } = useAuth();
  const { catalog: regions, loading: regionsLoading } = useMapRegions();
  // Title carries the signed-in organization's name — the chrome never
  // hardcodes a county. Falls back to the bare product framing pre-/auth/me.
  const commandCenterTitle = user?.tenant_name
    ? `${user.tenant_name} Command Center`
    : 'Command Center';
  const mapRegionsConfigured =
    user?.tenant_map_regions_configured !== false &&
    (regionsLoading || !regions.isEmpty);

  // Guided coach-mark tour, sequenced after the OnboardingModal. Activation is
  // gated on the county data having loaded: this view's entire body is behind a
  // `stats && ldStats && precincts` guard, and roles without analytics access
  // (CANVASSER) get a 403 and see only the header + error banner. Firing the
  // tour for them would just poll for anchors that are never coming.
  const { hasSeen, markSeen, seen } = useOnboarding();
  const [tourActive, setTourActive] = useState(false);
  const onboardingDisabled =
    typeof window !== 'undefined' &&
    ((window as any).__PCGOP_DISABLE_ONBOARDING__ === true ||
      (import.meta as any).env?.VITE_DISABLE_ONBOARDING === '1');

  // Subregion display mode — table is the canonical scan view (right-aligned
  // numerics, LD-grouped, sortable). Cards stay available for users who want
  // the rich per-tile detail. Persisted to localStorage so the choice sticks.
  const [subregionView, setSubregionView] = useState<'cards' | 'table'>(() => {
    if (typeof window === 'undefined') return 'table';
    const v = window.localStorage.getItem('pcgop:subregion-view');
    return v === 'cards' || v === 'table' ? v : 'table';
  });
  const setSubregionViewMode = (m: 'cards' | 'table') => {
    setSubregionView(m);
    try {
      window.localStorage.setItem('pcgop:subregion-view', m);
    } catch {
      /* ignore quota / private mode */
    }
  };

  // Lifted sort state — shared between Cards mode and the controlled
  // SubregionTable so toggling display doesn't lose the user's choice and
  // both modes scan in the same order.
  const [subregionSortKey, setSubregionSortKey] = useState<SubregionSortKey>(() => {
    if (typeof window === 'undefined') return 'avg_score';
    const v = window.localStorage.getItem('pcgop:subregion-sort-key');
    return v === 'avg_score' || v === 'total' || v === 'active_pct' || v === 'primary_pct'
      ? (v as SubregionSortKey)
      : 'avg_score';
  });
  const [subregionSortDir, setSubregionSortDir] = useState<SubregionSortDir>(() => {
    if (typeof window === 'undefined') return 'desc';
    const v = window.localStorage.getItem('pcgop:subregion-sort-dir');
    return v === 'asc' || v === 'desc' ? (v as SubregionSortDir) : 'desc';
  });
  const applySubregionSort = (k: SubregionSortKey, d: SubregionSortDir) => {
    setSubregionSortKey(k);
    setSubregionSortDir(d);
    try {
      window.localStorage.setItem('pcgop:subregion-sort-key', k);
      window.localStorage.setItem('pcgop:subregion-sort-dir', d);
    } catch {
      /* ignore */
    }
  };

  // Group state — only 'ld' for now, but structured so 'none' can be added
  // later without touching consumers. (out of scope for this change.)
  type SubregionGroup = 'ld';
  const [subregionGroup, setSubregionGroup] = useState<SubregionGroup>('ld');

  // Sticky control row — publishes its measured height to :root as
  // --subregion-controls-h so the SubregionTable's sticky <thead> + LD
  // section headers can stack underneath us at the right offset. Cleared on
  // unmount so other views don't see a stale offset.
  const subregionControlsRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = subregionControlsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const publish = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        '--subregion-controls-h',
        `${Math.round(h)}px`,
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.setProperty('--subregion-controls-h', '0px');
    };
    // `loading` re-runs the effect once data lands and the ref-attached div
    // actually mounts; without it the effect's first run bails on a null ref
    // and never re-fires (subregionView alone can't pull it back).
  }, [subregionView, loading]);

  // Combined sort dropdown value — encodes both key and dir so a single
  // <select> can drive both. Mirrors the order requested in the brief.
  const sortSelectValue = `${subregionSortKey}:${subregionSortDir}`;
  const onSortSelectChange = (v: string) => {
    const [k, d] = v.split(':') as [SubregionSortKey, SubregionSortDir];
    applySubregionSort(k, d);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      client.get<VoterStats>('/voters/stats'),
      client.get<LegislativeDistrictStats[]>('/voters/by-legislative-district'),
      client.get<PrecinctStats[]>('/voters/by-precinct?limit=200'),
    ])
      .then(([s, lds, precs]) => {
        if (cancelled) return;
        setStats(s.data);
        setLdStats(lds.data);
        setPrecincts(precs.data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 401)
          setError('Not authenticated — log in to see the live county map.');
        else setError(err?.message ?? 'Unknown error loading county map.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // -----------------------------------------------------------------------
  // Client-side subregion rollup: for each turf in the catalog, sum over
  // the precincts in that SR from the live precinct list. Subregion is
  // a turf-assignment construct, not a voter-roll column, so the rollup
  // has to happen here.
  // -----------------------------------------------------------------------
  const subregionMap = useMemo(() => {
    if (!precincts) return [];

    // Precinct ids are strings on both sides of this join — the DB column is a
    // String and `precinctsAsStrings` is the catalog's untouched wire copy —
    // so no coercion is needed here any more.
    const byNum: Record<string, PrecinctStats> = {};
    for (const p of precincts) {
      byNum[String(p.precinct_num)] = p;
    }

    return regions.keys.map((code) => {
      const r = regions.byKey[code];
      const rows: PrecinctStats[] = r.precinctsAsStrings
        .map((n) => byNum[n])
        .filter(Boolean);
      const total = rows.reduce((s, p) => s + p.total_voters, 0);
      const active = rows.reduce((s, p) => s + p.active_voters, 0);
      const primary = rows.reduce((s, p) => s + p.primary_voters, 0);
      const scoreSum = rows.reduce(
        (s, p) => s + p.avg_score * p.total_voters,
        0,
      );
      const superVoters = rows.reduce((s, p) => s + p.super_voters, 0);
      return {
        code,
        ld: r.ld,
        name: r.name,
        description: r.description,
        mapFile: r.mapFile,
        total,
        active_pct: total ? (active * 100) / total : 0,
        primary_pct: total ? (primary * 100) / total : 0,
        avg_score: total ? scoreSum / total : 0,
        super_voter_pct: total ? (superVoters * 100) / total : 0,
        precinct_count: rows.length,
      };
    });
  }, [precincts, regions]);

  // -----------------------------------------------------------------------
  // Client-side LD enrichment: merge live backend LD stats with the turf
  // catalog's subregion / precinct counts so each card can show
  // "N precincts · M subregions" like the old version did.
  // -----------------------------------------------------------------------
  const ldCards = useMemo(() => {
    if (!ldStats) return [];
    const staticCounts: Record<
      string,
      { precincts: number; subregions: number }
    > = {};
    for (const code of regions.keys) {
      const r = regions.byKey[code];
      const ldKey = String(r.ld);
      if (!staticCounts[ldKey]) {
        staticCounts[ldKey] = { precincts: 0, subregions: 0 };
      }
      staticCounts[ldKey].subregions += 1;
      staticCounts[ldKey].precincts += r.precinctsAsStrings.length;
    }
    return ldStats
      .map((ld) => ({
        ...ld,
        precincts: staticCounts[ld.district_key]?.precincts ?? 0,
        subregions: staticCounts[ld.district_key]?.subregions ?? 0,
      }))
      .sort((a, b) => parseInt(a.district_key, 10) - parseInt(b.district_key, 10));
  }, [ldStats, regions]);

  // The tour's anchors live inside the `stats && ldStats && precincts` block in
  // the render, so both activation and mount key off the same condition.
  const tourAnchorsReady = Boolean(stats && ldStats && precincts);

  // Modal already dismissed on a prior visit but the tour never done — start it
  // once the data (and therefore the anchors) are actually there. A genuine
  // first visit instead hands off from the modal's onClose. Keyed on the
  // seen-list so it re-evaluates when /auth/me resolves or the account changes.
  //
  // MUST stay above the early loading return below — React requires an
  // unconditional hook order, and the skeleton path returns before it.
  useEffect(() => {
    if (onboardingDisabled || !tourAnchorsReady) return;
    if (hasSeenOnboarding('mapview') && !hasSeen(MAP_TOUR.id)) {
      setTourActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seen.join('|'), tourAnchorsReady]);

  // This view's LD cards, Subregion Map, and Command Center headline all
  // resolve through the turf catalog. A tenant whose turfs have not been
  // loaded gets one empty state instead of a page of zeroes — and, since the
  // catalog is tenant-scoped, never another county's turf names. Preflight
  // blocks the launch wizard for the same reason.
  //
  // Bails BEFORE the loading branch so an unconfigured tenant never sees a
  // skeleton for content that will never render. `mapRegionsConfigured` stays
  // true while the catalog is still in flight, so a configured tenant sees the
  // skeleton rather than a flash of this.
  if (!mapRegionsConfigured) {
    return (
      <div className="space-y-8">
        <div className="bg-bg-elevated rounded-xl p-8 border border-border-base">
          <h2 className="text-lg font-bold text-fg-primary mb-2">
            Turfs not yet configured
          </h2>
          <p className="text-sm text-fg-secondary">
            This tenant does not have precinct or subregion turf data loaded
            yet. The Voters list, Coverage, Optimizer, and Canvass surfaces
            work without turf data; the county overview requires it.
          </p>
          <p className="text-sm text-fg-secondary mt-3">
            An admin needs to load this tenant&rsquo;s turfs. Contact platform
            support if you are unsure how.
          </p>
        </div>
      </div>
    );
  }

  // ---------- early loading return (no data yet) ----------
  // `regionsLoading` joins the condition so the Subregion Map never renders
  // its empty-turf state for the beat between the voter stats landing and the
  // turf catalog landing. Showing the skeleton a moment longer beats showing a
  // county with no turfs in it and then repainting.
  if (regionsLoading || (loading && !stats && !ldStats && !precincts)) {
    return (
      <div className="space-y-6">
        <ViewHeader
          title={commandCenterTitle}
          tagline="Every number is a doorway. Maps are the spine. Nothing is a dead end."
        />
        <StatsGridSkeleton count={6} />
        <Skeleton className="w-full h-[400px]" />
      </div>
    );
  }

  const activePct = stats?.total_voters
    ? (stats.active_voters * 100) / stats.total_voters
    : 0;
  const primaryPct = stats?.total_voters
    ? (stats.primary_voters * 100) / stats.total_voters
    : 0;

  return (
    <div className="space-y-8">
      <OnboardingModal
        viewKey="mapview"
        title="County Command Center"
        intro="This is the top of the drilldown. Numbers come from Postgres in real time. Click any Legislative District or Subregion card to drill in — every number is a doorway, nothing here is a dead end."
        termIds={[
          'tier_overview',
          'voter_score',
          'active_voter',
          'primary_voter',
          'subregion',
        ]}
        workflows={[
          'Click an LD card to see precinct-level stats and turnout history.',
          'Click a subregion tile to see who lives in that turf and how they vote.',
          'Open the Turnout Heatmap to compare election-by-election patterns.',
          'Open the Drop-Off Detector to find voters who stopped showing up.',
        ]}
        forceOpen={forceOnboard}
        onClose={() => {
          const wasForced = forceOnboard;
          setForceOnboard(false);
          // First-visit dismissal (NOT a Help-button reopen) hands off to the
          // tour — but only if its anchors actually rendered.
          if (
            !wasForced &&
            !onboardingDisabled &&
            tourAnchorsReady &&
            !hasSeen(MAP_TOUR.id)
          ) {
            setTourActive(true);
          }
        }}
      />

      {tourActive && tourAnchorsReady && (
        <CoachMarks
          tour={MAP_TOUR}
          onFinish={() => {
            setTourActive(false);
            void markSeen(MAP_TOUR.id);
          }}
          // Anchors never mounted — hide without marking seen so it re-offers.
          onAbort={() => setTourActive(false)}
        />
      )}

      {/* Header */}
      <ViewHeader
        title={commandCenterTitle}
        tagline="Every number is a doorway. Maps are the spine. Nothing is a dead end."
        actions={
          <div data-tour="onboarding-help">
            <OnboardingHelpButton onClick={() => setForceOnboard(true)} />
          </div>
        }
      />

      {/* Error banner (if data failed to load) */}
      {error && (
        <div className="rounded-xl border border-status-danger bg-status-danger-bg/40 p-6 text-status-danger">
          <div className="font-semibold mb-1">Couldn't load the county map</div>
          <div className="text-sm text-status-danger/80">{error}</div>
        </div>
      )}

      {/* Tier legend strip + visual indicator key — only show if data loaded */}
      {stats && ldStats && precincts && (
        <>
          <TierLegend />
          <VisualKey />
        </>
      )}

      {/* County Overview, Legislative Districts, Subregion Map — only show if data loaded */}
      {stats && ldStats && precincts && (
        <>
          {/* County Overview — live */}
          <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
                County Overview
              </h2>
              <span className="text-xs text-fg-muted">
                County-wide totals
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-bg-base rounded-lg p-4 border border-border-base">
                <MetricLabel termId="data_freshness">Total Voters</MetricLabel>
                <div className="text-2xl font-bold text-fg-primary mt-1 tabular-nums">
                  {stats.total_voters.toLocaleString()}
                </div>
                <div className="text-xs text-fg-muted mt-1">County-wide total</div>
              </div>
              <div className="bg-bg-base rounded-lg p-4 border border-border-base">
                <MetricLabel termId="active_voter">Active %</MetricLabel>
                <div className="text-2xl font-bold text-fg-primary mt-1 tabular-nums">
                  {activePct.toFixed(1)}%
                </div>
                <div className="mt-2">
                  <BarMeter value={activePct} tierColor={false} />
                </div>
              </div>
              <div className="bg-bg-base rounded-lg p-4 border border-border-base">
                <MetricLabel termId="avg_score">Avg Score</MetricLabel>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-2xl font-bold text-fg-primary tabular-nums">
                    {stats.average_score.toFixed(1)}
                  </div>
                  <TierPill score={stats.average_score} />
                </div>
                <div className="mt-2">
                  <BarMeter value={stats.average_score} />
                </div>
              </div>
              <div className="bg-bg-base rounded-lg p-4 border border-border-base">
                <MetricLabel termId="primary_voter">Primary Voters</MetricLabel>
                <div className="text-2xl font-bold text-fg-primary mt-1 tabular-nums">
                  {stats.primary_voters.toLocaleString()}
                </div>
                <div className="text-xs text-fg-muted mt-0.5 tabular-nums">
                  {primaryPct.toFixed(1)}% of total
                </div>
                <div className="mt-2">
                  <BarMeter value={primaryPct} tierColor={false} />
                </div>
              </div>
            </div>
          </div>

          {/* Legislative Districts — live */}
          <div className="bg-bg-raised rounded-xl p-6 border border-border-base" data-tour="map-ld-cards">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold text-fg-primary flex items-center gap-2">
            Legislative Districts
            <InfoTip termId="legislative_district" />
          </h2>
          <span className="text-xs text-fg-muted">
            Click any district to drill in
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ldCards.map((st) => {
            const ldNum = parseInt(st.district_key, 10);
            const ldPeers = computePeerBaselines(ldCards as any);
            const scoreRank = rankWithin(
              ldCards as any,
              (r: any) => r.avg_score,
              st.avg_score,
              'higher-better',
            );
            // Tier classification — color used in the score mini-bar so the
            // separate WARM/HOT/COOL chip is unnecessary chrome.
            const tierBgClass =
              st.avg_score >= 75
                ? 'bg-tier-anchor'
                : st.avg_score >= 60
                  ? 'bg-tier-hot'
                  : st.avg_score >= 40
                    ? 'bg-tier-warm'
                    : 'bg-tier-cool';
            const tierName =
              st.avg_score >= 75
                ? 'Anchor'
                : st.avg_score >= 60
                  ? 'Hot'
                  : st.avg_score >= 40
                    ? 'Warm'
                    : 'Cool';
            const scoreDelta = st.avg_score - stats.average_score;
            const primaryDelta = st.primary_voter_pct - primaryPct;
            return (
              <button
                key={st.district_key}
                onClick={() => onDrill('ld', ldNum)}
                className="group relative bg-bg-base hover:bg-bg-elevated rounded-lg overflow-hidden border border-border-base hover:border-accent transition cursor-pointer text-left flex items-stretch shadow-ds-sm"
                aria-label={`Drill into ${LD_NAMES[ldNum] || `LD ${st.district_key}`}, ${st.total_voters.toLocaleString()} voters, score ${st.avg_score.toFixed(1)} ${tierName}`}
              >
                {/* Brand stripe — same vocabulary as ViewHeader. Brightens on hover. */}
                <div
                  className="w-1 shrink-0 bg-border-subtle group-hover:bg-accent transition-colors"
                  aria-hidden="true"
                />
                <div className="flex-1 p-4 min-w-0">
                  {/* Identity row: name + click affordance */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="text-sm font-semibold text-fg-primary truncate">
                      {LD_NAMES[ldNum] || `LD ${st.district_key}`}
                    </div>
                    <ChevronRight
                      className="w-4 h-4 text-fg-muted group-hover:text-accent-fg transition-colors flex-shrink-0"
                      aria-hidden="true"
                    />
                  </div>

                  {/* Hero metric — voter count, very large. Followed by a tight
                      label-pair subtitle ("voters · N precincts · M subregions") so
                      the eye reads "this LD has 32,264 voters across 12 precincts
                      in 5 subregions" as one breath. */}
                  <div className="text-3xl font-bold text-fg-primary tabular-nums leading-none">
                    {st.total_voters.toLocaleString()}
                  </div>
                  <div className="text-xs text-fg-muted mt-1">
                    voters · {st.precincts} precinct{st.precincts === 1 ? '' : 's'} · {st.subregions} subregion{st.subregions === 1 ? '' : 's'}
                  </div>

                  {/* Metric table — label/value rows with right-aligned numbers
                      so users can eye-compare the three LD cards side by side.
                      Hairline dividers (top + between + bottom) and symmetric
                      py-2.5 give each metric the same vertical rhythm. */}
                  <div className="mt-4 divide-y divide-border-subtle border-y border-border-subtle text-sm">
                    {/* Score row: number, mini-bar, tier name, delta vs county */}
                    <div className="py-2.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs text-fg-muted uppercase tracking-wide">Score</span>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-fg-primary tabular-nums">
                            {st.avg_score.toFixed(1)}
                          </span>
                          <div
                            className="relative h-1.5 w-16 rounded-full bg-bg-elevated overflow-hidden"
                            role="img"
                            aria-label={`Score ${st.avg_score.toFixed(1)} of 100, ${tierName} tier`}
                          >
                            <div
                              className={`absolute inset-y-0 left-0 ${tierBgClass}`}
                              style={{ width: `${Math.max(0, Math.min(100, st.avg_score))}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5 text-xs">
                        <span className="text-fg-muted">{tierName} · #{scoreRank.rank} of {scoreRank.total}</span>
                        <span
                          className={`tabular-nums font-semibold ${
                            Math.abs(scoreDelta) < 0.05
                              ? 'text-fg-muted'
                              : scoreDelta > 0
                                ? 'text-status-success'
                                : 'text-status-danger'
                          }`}
                        >
                          {Math.abs(scoreDelta) < 0.05 ? '— ' : scoreDelta > 0 ? '↑ +' : '↓ '}
                          {scoreDelta > 0
                            ? scoreDelta.toFixed(1)
                            : Math.abs(scoreDelta).toFixed(1)}
                          <span className="text-fg-muted font-normal"> vs county</span>
                        </span>
                      </div>
                    </div>

                    {/* Primary turnout row: percentage + delta vs county */}
                    <div className="py-2.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-xs text-fg-muted uppercase tracking-wide">Primary turnout</span>
                        <span className="font-medium text-fg-primary tabular-nums">
                          {st.primary_voter_pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-end gap-2 mt-0.5 text-xs">
                        <span
                          className={`tabular-nums font-semibold ${
                            Math.abs(primaryDelta) < 0.05
                              ? 'text-fg-muted'
                              : primaryDelta > 0
                                ? 'text-status-success'
                                : 'text-status-danger'
                          }`}
                        >
                          {Math.abs(primaryDelta) < 0.05 ? '— ' : primaryDelta > 0 ? '↑ +' : '↓ '}
                          {primaryDelta > 0
                            ? primaryDelta.toFixed(1)
                            : Math.abs(primaryDelta).toFixed(1)}%
                          <span className="text-fg-muted font-normal"> vs county</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Subregion Map — live (rolled up client-side from precinct list) */}
      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <div
          ref={subregionControlsRef}
          className="sticky top-[var(--app-header-h)] z-20 -mx-4 px-4 sm:-mx-6 sm:px-6 py-3 mb-6 bg-bg-raised border-b border-border-base shadow-ds-sm flex items-center justify-between flex-wrap gap-3"
        >
          <h2
            className="text-lg font-bold text-fg-primary flex items-center gap-2"
            data-tour="map-subregions"
          >
            Subregion Map
            <InfoTip termId="subregion" />
          </h2>
          {/* Control row — Sort / Group / View. Drives both Cards and Table
              modes via lifted state, so toggling display preserves the user's
              chosen order. */}
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <label className="flex items-center gap-1.5">
              <span className="text-fg-muted uppercase tracking-wide">Sort by</span>
              <select
                value={sortSelectValue}
                onChange={(e) => onSortSelectChange(e.target.value)}
                className="bg-bg-base border border-border-base text-fg-primary rounded px-2 py-1 hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent min-h-9"
                aria-label="Sort subregions by"
              >
                <option value="avg_score:desc">Score (high → low)</option>
                <option value="avg_score:asc">Score (low → high)</option>
                <option value="total:desc">Voters (high → low)</option>
                <option value="active_pct:desc">Active % (high → low)</option>
                <option value="primary_pct:desc">Primary % (high → low)</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5">
              <span className="text-fg-muted uppercase tracking-wide">Group</span>
              <select
                value={subregionGroup}
                onChange={(e) => setSubregionGroup(e.target.value as SubregionGroup)}
                className="bg-bg-base border border-border-base text-fg-primary rounded px-2 py-1 hover:bg-bg-elevated focus:outline-none focus:ring-2 focus:ring-accent min-h-9"
                aria-label="Group subregions by"
              >
                <option value="ld">LD</option>
              </select>
            </label>
            {/* View toggle — Cards (rich detail) vs Table (sortable scan) */}
            <div
              className="inline-flex rounded-md border border-border-base bg-bg-base p-0.5"
              role="tablist"
              aria-label="Subregion display mode"
            >
              <button
                type="button"
                role="tab"
                aria-selected={subregionView === 'table'}
                onClick={() => setSubregionViewMode('table')}
                className={`px-3 py-1 rounded text-xs font-semibold transition-colors min-h-9 ${
                  subregionView === 'table'
                    ? 'bg-accent text-white'
                    : 'text-fg-secondary hover:text-fg-primary hover:bg-bg-elevated'
                }`}
              >
                Table
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={subregionView === 'cards'}
                onClick={() => setSubregionViewMode('cards')}
                className={`px-3 py-1 rounded text-xs font-semibold transition-colors min-h-9 ${
                  subregionView === 'cards'
                    ? 'bg-accent text-white'
                    : 'text-fg-secondary hover:text-fg-primary hover:bg-bg-elevated'
                }`}
              >
                Cards
              </button>
            </div>
          </div>
        </div>

        {subregionView === 'table' ? (
          <SubregionTable
            subregions={subregionMap as any}
            ldCards={ldCards as any}
            countyAverageScore={stats.average_score}
            onDrill={(target, code) => onDrill(target, code)}
            sortKey={subregionSortKey}
            sortDir={subregionSortDir}
            onSortChange={applySubregionSort}
          />
        ) : (
          <div className="space-y-6">
          {/* LD-grouped sections — mirrors SubregionTable's grouping so toggling
              Cards ↔ Table preserves the user's mental hierarchy. Within each LD
              group, subregions sort by score descending so highest-priority
              targets read first. */}
          {ldCards.map((ldCard: any) => {
            const ld = parseInt(ldCard.district_key, 10);
            const ldSubs = subregionMap
              .filter((s: any) => s.ld === ld)
              .sort((a: any, b: any) => {
                const av = a[subregionSortKey];
                const bv = b[subregionSortKey];
                const cmp = av === bv ? 0 : av < bv ? -1 : 1;
                return subregionSortDir === 'desc' ? -cmp : cmp;
              });
            if (!ldSubs.length) return null;
            return (
              <section key={ld}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap pb-2 mb-3 border-b border-border-base">
                  <div className="text-xs font-bold uppercase tracking-wider text-fg-secondary">
                    {LD_NAMES[ld] || `LD ${ldCard.district_key}`}
                    <span className="ml-2 font-normal normal-case tracking-normal text-fg-muted">
                      · {ldSubs.length} subregion{ldSubs.length === 1 ? '' : 's'}
                      {' · '}
                      <span className="tabular-nums">{ldCard.total_voters.toLocaleString()}</span> voters
                      {' · LD avg score '}
                      <span className="tabular-nums">{ldCard.avg_score.toFixed(1)}</span>
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {ldSubs.map((sr: any) => {
                const srCode = sr.code;
                // retained for future legend usage; LD identity is now textual
                const color = LD_COLORS[sr.ld] || '#dc2626';
                const textColor = LD_TEXT_COLORS[sr.ld] || '#f87171';
                // Parent LD baseline for this subregion
                const parentLd = ldCards.find(
                  (l: any) => parseInt(l.district_key, 10) === sr.ld,
                );
                // Peer group: other subregions in the same LD
                const sameLdPeers = subregionMap.filter(
                  (s: any) => s.ld === sr.ld,
                );
                const peerBl = computePeerBaselines(
                  sameLdPeers.map((p: any) => ({
                    avg_score: p.avg_score,
                    active_pct: p.active_pct,
                    primary_voter_pct: p.primary_pct,
                  })),
                );
                const srRank = rankWithin(
                  sameLdPeers as any,
                  (r: any) => r.avg_score,
                  sr.avg_score,
                  'higher-better',
                );
                // Tier classification (used inline by the score row + bar fill).
                const tierBgClass =
                  sr.avg_score >= 75
                    ? 'bg-tier-anchor'
                    : sr.avg_score >= 60
                      ? 'bg-tier-hot'
                      : sr.avg_score >= 40
                        ? 'bg-tier-warm'
                        : 'bg-tier-cool';
                const tierName =
                  sr.avg_score >= 75
                    ? 'Anchor'
                    : sr.avg_score >= 60
                      ? 'Hot'
                      : sr.avg_score >= 40
                        ? 'Warm'
                        : 'Cool';
                const ldBaseline = parentLd
                  ? parentLd.avg_score
                  : stats.average_score;
                const delta = sr.avg_score - ldBaseline;
                const deltaIsFlat = Math.abs(delta) < 0.05;
                const deltaIsUp = delta > 0;
                return (
                  <div
                    key={srCode}
                    className="group bg-bg-base hover:bg-bg-elevated rounded-lg border-2 border-border-base hover:border-accent transition flex flex-col"
                  >
                    {/* Click target — drills to subregion. Identity / hero+supporting / score row. */}
                    <button
                      onClick={() => onDrill('subregion', srCode)}
                      className="text-left p-3 pb-2 flex-1"
                    >
                      {/* Identity row: code · name (left) · rank badge (right) */}
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-sm font-semibold text-fg-primary truncate">
                          <span className="font-mono text-xs text-fg-muted">{srCode}</span>
                          <span className="mx-1.5 text-fg-muted">·</span>
                          {sr.name}
                        </div>
                        <RankBadge
                          rank={srRank.rank}
                          total={srRank.total}
                          label="in LD"
                        />
                      </div>

                      {/* Hero — voter count with explicit unit */}
                      <div className="text-2xl font-bold text-fg-primary tabular-nums leading-none">
                        {sr.total.toLocaleString()}
                        <span className="text-xs font-normal text-fg-muted ml-1.5">voters</span>
                      </div>

                      {/* Supporting — label-suffix pair, easier to skim than "Active: X%" */}
                      <div className="text-xs text-fg-muted mt-1">
                        <span className="tabular-nums">{sr.active_pct.toFixed(1)}%</span> active
                        <span className="mx-1">·</span>
                        <span className="tabular-nums">{sr.primary_pct.toFixed(1)}%</span> primary
                      </div>

                      {/* Combined score row — label · value · bar · delta vs LD · tier name */}
                      <div className="mt-3 flex items-center gap-2 text-sm">
                        <span className="text-xs text-fg-muted uppercase tracking-wide">
                          Score
                        </span>
                        <span className="font-bold text-fg-primary tabular-nums">
                          {sr.avg_score.toFixed(1)}
                        </span>
                        <div
                          className="relative h-1.5 w-12 rounded-full bg-bg-elevated overflow-hidden flex-shrink-0"
                          role="img"
                          aria-label={`Score ${sr.avg_score.toFixed(1)} of 100, ${tierName} tier`}
                        >
                          <div
                            className={`absolute inset-y-0 left-0 ${tierBgClass}`}
                            style={{ width: `${Math.max(0, Math.min(100, sr.avg_score))}%` }}
                          />
                        </div>
                        {deltaIsFlat ? (
                          <span className="text-xs text-fg-muted tabular-nums">—</span>
                        ) : (
                          <span
                            className={`text-xs font-semibold tabular-nums ${deltaIsUp ? 'text-status-success' : 'text-status-danger'}`}
                          >
                            {deltaIsUp ? '↑ +' : '↓ '}
                            {Math.abs(delta).toFixed(1)}
                          </span>
                        )}
                        <span className="text-[11px] uppercase tracking-wide text-fg-muted ml-auto">
                          {tierName}
                        </span>
                      </div>
                    </button>

                    {/* Actions row — drill chevron is the persistent affordance;
                        Map / Route reveal on hover or keyboard focus to keep the
                        resting card data-only. Buttons live outside the click
                        target <button> so child anchor clicks don't bubble to
                        onDrill (and stopPropagation belt-and-suspenders). */}
                    <div className="flex items-center gap-1 px-3 pb-3 pt-1">
                      <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 transition-opacity duration-150">
                        <a
                          href={`/maps/${sr.mapFile}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-bg-elevated hover:bg-bg-raised text-fg-secondary hover:text-fg-primary px-2 py-0.5 rounded text-xs inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-accent"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Map
                        </a>
                        {GMAPS[srCode]?.shifts?.[0]?.url && (
                          <a
                            href={GMAPS[srCode].shifts[0].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-bg-elevated hover:bg-bg-raised text-fg-secondary hover:text-fg-primary px-2 py-0.5 rounded text-xs inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-accent"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Route
                          </a>
                        )}
                      </div>
                      <ChevronRight
                        className="w-4 h-4 text-fg-muted group-hover:text-accent-fg transition-colors ml-auto"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                );
              })}
                </div>
              </section>
            );
          })}
          </div>
        )}
          </div>
        </>
      )}

    </div>
  );
};
